// src/store/slices/newbieSlice.js
/**
 * newbieSlice & fetchNewbie thunk
 * --------------------------------------------------------------------
 * 목적:
 *  - 신규 가입자(방문) 데이터(일/주/월), 유효 날짜 목록(validDateList),
 *    기준 날짜(date), 매장 ID, 로딩/에러 상태를 Redux에서 관리.
 *  - fetchNewbie: 주어진 localDate 기준으로 신규 가입자 데이터를 백엔드에서 조회.
 *
 * API 계약(예상 스키마):
 *  GET /api/visit/new?localDate=YYYY-MM-DD
 *  -> {
 *       store_id: number | string,
 *       daily:   Array<행>,
 *       weekly:  Array<행>,
 *       monthly: Array<행>,
 *       valid_date_list: Array<string> // 선택 가능한 날짜(달력 표시용)
 *     }
 *
 * 반환 payload(Thunk → Reducer):
 *  {
 *    date: localDate,
 *    store_id,
 *    visitDetail: { daily, weekly, monthly },
 *    validDateList
 *  }
 *
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { mokiApi } from "../../services/loginApi";
import { formatDate } from "../../functions/DateConverter";

// 백엔드 API를 호출하여 신규 가입자 데이터를 가져오는 thunk
export const fetchNewbie = createAsyncThunk(
  "newbie/fetchNewbie",
  async (localDate) => {
    // 1) 인증 토큰을 Authorization 헤더에 설정(있을 때만)
    const token = sessionStorage.getItem("accessToken");
    if (token) {
      mokiApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // 2) 신규 가입자 API 호출(날짜 파라미터 전달)
    const response = await mokiApi.get("/api/visit/new", {
      params: { localDate },
    });
    const apiData = response.data;

    // 3) 슬라이스가 기대하는 payload 형태로 매핑해 반환
    return {
      date: localDate,
      store_id: apiData.store_id,
      visitDetail: {
        daily: apiData.daily,
        weekly: apiData.weekly,
        monthly: apiData.monthly,
      },
      validDateList: apiData.valid_date_list,
    };
  }
);

// Redux Slice: 상태/동기 리듀서/비동기 리듀서 정의
const newbieSlice = createSlice({
  name: "newbie",

  // 초기 상태
  initialState: {
    date: formatDate(new Date()), // 기준 날짜(문자열)
    storeId: null, // 매장 식별자
    visitDetail: { daily: [], weekly: [], monthly: [] }, // 신규 가입자 데이터(기간별)
    validDateList: [], // 선택 가능한 날짜 목록
    isLoading: false, // 로딩 상태
    error: null, // 에러 객체
  },

  // 동기 리듀서
  reducers: {
    // 상태 전체 초기화(로그아웃/리셋 시 사용)
    clearNewbie: (state) => {
      state.date = null;
      state.storeId = null;
      state.visitDetail = { daily: [], weekly: [], monthly: [] };
      state.validDateList = [];
      state.isLoading = false;
      state.error = null;
    },

    // 기준 날짜 변경(문자열 권장; 훅에서 이 값 변화를 감지해 재조회)
    setDate: (state, action) => {
      state.date = action.payload;
    },
  },

  // 비동기 리듀서(thunk 상태 변화 처리)
  extraReducers: (builder) =>
    builder
      // 요청 시작 → 로딩 on, 에러 초기화
      .addCase(fetchNewbie.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // 요청 성공 → payload로 상태 갱신
      .addCase(fetchNewbie.fulfilled, (state, { payload }) => {
        state.date = payload.date;
        state.storeId = payload.store_id;
        state.visitDetail = payload.visitDetail || {
          daily: [],
          weekly: [],
          monthly: [],
        };
        state.validDateList = payload.validDateList || [];
        state.isLoading = false;
      })
      // 요청 실패 → 로딩 off, 에러 저장
      .addCase(fetchNewbie.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error; // { name, message, ... }
      }),
});

// 액션 export
export const { clearNewbie, setDate } = newbieSlice.actions;

// 리듀서 export
export default newbieSlice.reducer;
